/**
 * React-контекст авторизации.
 * Хранит текущего пользователя, предоставляет login/logout.
 */

import React, {
    createContext, useContext, useState, useEffect, ReactNode,
} from 'react';
import { api } from './api';

/* ---------- Типы ---------- */
interface User {
    username: string;
    role: string;
}

interface AuthCtx {
    user: User | null;
    loading: boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
}

/* ---------- Контекст ---------- */
const AuthContext = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser]       = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    /* При первом рендере — проверяем токен */
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            api.get<User>('/auth/me')
                .then(setUser)
                .catch(() => localStorage.removeItem('token'))
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    const login = async (username: string, password: string) => {
        const res = await api.post<{
            access_token: string;
            username: string;
            role: string;
        }>('/auth/login', { username, password });

        localStorage.setItem('token', res.access_token);
        setUser({ username: res.username, role: res.role });
    };

    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthCtx {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth должен быть внутри AuthProvider');
    return ctx;
}
